import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    PepDetailStatusesEnum,
    RiskActionsEnum,
    IndividualPepDetailsService,
    IndividualPepDetailInterface,
    RiskClassModel,
    IndividualExtendedModel,
    IndividualPepDetailModel,
    IndividualPepDetailInputModel,
} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {Helper, SelectControl} from '../../../../shared/classes';

import {PepDetailsFormErrorStatesEnum, PepManagerApprovalEnum} from '../../enums';

import {
    PepDetailsControls,
    PepDetailsStatusSelector,
    PepDetailsApprovalSelector,
    PepDetailsFormArraySelector,
    PepDetailsRiskClassificationSelector,
} from '../../../../../assets/descriptors';

export class PepDetailsForm {
    private readonly individual: IndividualExtendedModel;
    private readonly individualPepDetailsService: IndividualPepDetailsService;
    private readonly riskClassifications: RiskClassModel[];

    public form: FormGroup;
    public controls: SelectControl[];
    public formArraySelector: string;

    constructor(
        individual: IndividualExtendedModel,
        riskClassifications: RiskClassModel[],
        individualPepDetailsService: IndividualPepDetailsService
    ) {
        this.individual = individual;
        this.riskClassifications = riskClassifications;
        this.individualPepDetailsService = individualPepDetailsService;

        this.formArraySelector = PepDetailsFormArraySelector;
    }

    private initRiskClassificationControl(status: number, approval: number, control: SelectControl): SelectControl {
        const filteredClassification = this.riskClassifications
            .filter((classification: RiskClassModel) => {
                return this.individual.pepDetail &&
                    (this.individual.pepDetail.manualRiskClassification === classification.upperBound);
            });

        if (filteredClassification.length !== 0) {
            control.value = filteredClassification[0].upperBound;
        }

        control.options = this.riskClassifications.map((classification: RiskClassModel) => {
            return {
                label: classification.name,
                value: classification.upperBound
            };
        });

        let riskClassification = [];

        if (status) {
            if (approval !== PepManagerApprovalEnum.None) {
                if (approval !== PepManagerApprovalEnum.Yes) {
                    riskClassification = this.riskClassifications.filter((classification: RiskClassModel) => {
                        return classification.riskAction === RiskActionsEnum.Refuse;
                    });
                } else {
                    riskClassification = this.riskClassifications.filter((classification: RiskClassModel) => {
                        return classification.riskAction !== RiskActionsEnum.Refuse;
                    });
                }

                const riskScores = riskClassification.map((classification: RiskClassModel) => {
                    return classification.upperBound;
                });

                control.setValidators([Validators.required, InArrayValidator(riskScores)]);
            }
        }

        return control;
    }

    private createRegistrationRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        let riskScore = 0;
        let status = PepDetailStatusesEnum.Pending;
        let riskClassifications: RiskClassModel[] = [];

        const formGroup = (this.form.get(this.formArraySelector) as FormArray).at(0);

        switch (formGroup.value[PepDetailsApprovalSelector]) {
            case PepManagerApprovalEnum.No:
                status = PepDetailStatusesEnum.Refused;
                break;
            case PepManagerApprovalEnum.Yes:
                status = PepDetailStatusesEnum.Confirmed;

                riskClassifications = this.riskClassifications.filter((classification: RiskClassModel) => {
                    return classification.upperBound === formGroup.value[PepDetailsRiskClassificationSelector];
                });

                if (riskClassifications.length !== 0) {
                    riskScore = riskClassifications[0].upperBound;
                }
                break;
        }

        const inputModel = new IndividualPepDetailInputModel(
            this.individual.id,
            status,
            riskScore
        );

        return this.individualPepDetailsService.register(inputModel)
            .pipe(
                map((response: ApiResponse<ApiError[] | IndividualPepDetailInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<IndividualPepDetailInterface>(
                            response,
                            PepDetailsFormErrorStatesEnum.CreatePepDetailsFailed
                        );
                    }

                    const data = response.data as IndividualPepDetailInterface;

                    this.individual.pepDetail = new IndividualPepDetailModel(data);

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private createUpdateRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        let riskClassifications: RiskClassModel[] = [];

        const clonedModel = this.individual.pepDetail.serialize();
        const formGroup = (this.form.get(this.formArraySelector) as FormArray).at(0);

        this.individual.pepDetail.manualRiskClassification = 0;
        this.individual.pepDetail.reviewStatus = PepDetailStatusesEnum.Pending;

        switch (formGroup.value[PepDetailsApprovalSelector]) {
            case PepManagerApprovalEnum.No:
                this.individual.pepDetail.reviewStatus = PepDetailStatusesEnum.Refused;
                break;
            case PepManagerApprovalEnum.Yes:
                this.individual.pepDetail.reviewStatus = PepDetailStatusesEnum.Confirmed;

                riskClassifications = this.riskClassifications.filter((classification: RiskClassModel) => {
                    return classification.upperBound === formGroup.value[PepDetailsRiskClassificationSelector];
                });

                if (riskClassifications.length !== 0) {
                    this.individual.pepDetail.manualRiskClassification = riskClassifications[0].upperBound;
                }
                break;
        }

        return this.individualPepDetailsService.update(this.individual.pepDetail)
            .pipe(
                map((response: ApiResponse<ApiError[] | null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        this.individual.pepDetail = new IndividualPepDetailModel(clonedModel);

                        return Helper.errorsHelper().createErrorResponse<null>(
                            response,
                            PepDetailsFormErrorStatesEnum.UpdatePepDetailsFailed
                        );
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private createDeleteRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        return this.individualPepDetailsService.deleteById(this.individual.pepDetail.id)
            .pipe(
                map((response: ApiResponse<ApiError[] | null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<null>(
                            response,
                            PepDetailsFormErrorStatesEnum.DeletePepDetailsFailed
                        );
                    }

                    this.individual.pepDetail = null;

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    initForm(): Observable<boolean> {
        this.form = new FormGroup({});
        this.controls = [];

        const inlineForm = new FormGroup({});
        const inlineFormArray = new FormArray([]);

        const status = this.individual && this.individual.pepDetail ? 1 : 0;

        let approval = PepManagerApprovalEnum.None;

        if (this.individual.pepDetail && (this.individual.pepDetail.reviewStatus !== PepDetailStatusesEnum.Pending)) {
            approval = (this.individual.pepDetail.reviewStatus !== PepDetailStatusesEnum.Refused) ?
                PepManagerApprovalEnum.Yes : PepManagerApprovalEnum.No;
        }

        this.controls = PepDetailsControls().map((control: SelectControl) => {
            switch (control.name) {
                case PepDetailsStatusSelector:
                    control.value = status;
                    break;
                case PepDetailsApprovalSelector:
                    control.value = approval;

                    control.extendValidators(InArrayValidator([
                        PepManagerApprovalEnum.None,
                        PepManagerApprovalEnum.No,
                        PepManagerApprovalEnum.Yes
                    ]));
                    break;
                case PepDetailsRiskClassificationSelector:
                    control = this.initRiskClassificationControl(status, approval, control);
                    break;

            }

            inlineForm.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        inlineFormArray.push(inlineForm);

        this.form.addControl(this.formArraySelector, inlineFormArray);

        return of(true);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        return this.controls.map((control: SelectControl) => {
            const formGroup = (this.form.get(this.formArraySelector) as FormArray).at(0) as FormGroup;

            if (!formGroup.controls[control.name]) {
                return false;
            }

            const formControl = formGroup.controls[control.name];

            if (formControl.invalid) {
                const errorKeys = Object.keys(formControl.errors);

                const errors = errorKeys.map((key: string) => {
                    return messageFunction(control.placeholder)[key];
                });

                control.setErrors(errors);

                return false;
            }

            return true;
        }).indexOf(false) < 0;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> | null {
        const formGroup = (this.form.get(this.formArraySelector) as FormArray).at(0);

        if (formGroup.dirty) {
            if (!this.individual.pepDetail) {
                return this.createRegistrationRequest();
            }

            if (!formGroup.value[PepDetailsStatusSelector]) {
                return this.createDeleteRequest();
            }

            return this.createUpdateRequest();
        }

        return null;
    }
}
